import { ProfileOutlined } from "@ant-design/icons";
import {
  ActivityLogEventType,
  ActivityLog as ActivityLogModel,
} from "@models/ActivityLog";
import {
  fetchActivityLogs,
  fetchActivityLogsByLogName,
} from "@services/ActivityLogService";
import {
  PageTitle,
  PageTitleProps,
} from "@supplement-bacon/react-toolkit/components";
import { formatLogMessage, getColorForEvent } from "@utils/activityLogsToolbox";
import {
  App,
  Button,
  Card,
  CardProps,
  Empty,
  Skeleton,
  Space,
  Timeline,
  TimelineProps,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { FC, useEffect, useRef, useState } from "react";
import { DetailsCard } from "./DetailsCard";
import {
  ACTIVITY_LOG_PAGE_SIZE,
  hasMorePages,
  mergeLogPages,
} from "./timelinePaging";

const { Text, Title } = Typography;

type TimelineEntity =
  | "user"
  | "venue"
  | "event"
  | "event-participation"
  | "membership-subscription"
  | "membership-profile"
  | "business-profile"
  | "cluster"
  | "notification";

/**
 * Either the logs of one entity, or every log of a whole log name — the second form
 * is for entries whose subject the page cannot address by id, such as the membership
 * demo booklet, which hangs off a row of the settings table.
 */
type TimelineSource =
  | { entity: TimelineEntity; entityId: number; logName?: never }
  | { logName: string | string[]; entity?: never; entityId?: never };

type Props = TimelineSource & {
  title?: PageTitleProps["title"];
  cardProps?: CardProps;
};

// Shared by the initial-load effect and `handleLoadMore` so the two request
// paths (by log name, or by entity + id) can't drift apart.
const fetchActivityLogPage = (
  entity: TimelineEntity | undefined,
  entityId: number | undefined,
  logNames: string[],
  page: number,
) =>
  logNames.length
    ? fetchActivityLogsByLogName(logNames, page, ACTIVITY_LOG_PAGE_SIZE)
    : entity && entityId
      ? fetchActivityLogs(
          { entity, ids: [entityId] },
          page,
          ACTIVITY_LOG_PAGE_SIZE,
        )
      : null;

const ActivityLogTimeline: FC<Props> = ({
  entity,
  entityId,
  logName,
  title = (
    <Title level={5} className="m-0">
      <ProfileOutlined /> Activity logs
    </Title>
  ),
  cardProps,
}) => {
  const { message } = App.useApp();

  const [logs, setLogs] = useState<ActivityLogModel[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const logNames =
    logName === undefined ? [] : ([] as string[]).concat(logName);
  const logNamesKey = logNames.join(",");

  // Bumped every time the source (entity/entityId/log names) changes, so a response
  // for a now-stale source can be told apart and ignored.
  const sourceTokenRef = useRef(0);

  useEffect(() => {
    const token = ++sourceTokenRef.current;

    setLogs([]);
    setPage(1);
    setHasMore(false);
    setError(undefined);
    // A source change makes any in-flight request stale; its `finally` will be
    // skipped by the token guard below, so the flags it would have cleared must be
    // reset here instead — otherwise a new source can inherit a stuck spinner.
    setIsLoading(false);
    setIsLoadingMore(false);

    const request = fetchActivityLogPage(entity, entityId, logNames, 1);

    if (!request) return;

    setIsLoading(true);

    request
      .then((result) => {
        if (sourceTokenRef.current !== token) return;
        setLogs(result.data);
        setHasMore(hasMorePages(1, result.data.length, result.totalRecords));
      })
      .catch((error) => {
        if (sourceTokenRef.current !== token) return;
        setError(error);
      })
      .finally(() => {
        if (sourceTokenRef.current !== token) return;
        setIsLoading(false);
      });
    // Keyed on the names, not the array, which a caller may rebuild each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, entityId, logNamesKey]);

  const handleLoadMore = () => {
    const token = sourceTokenRef.current;
    const nextPage = page + 1;

    const request = fetchActivityLogPage(entity, entityId, logNames, nextPage);

    if (!request) return;

    setIsLoadingMore(true);

    request
      .then((result) => {
        if (sourceTokenRef.current !== token) return;
        setLogs((previous) => mergeLogPages(previous, result.data));
        setHasMore(
          hasMorePages(nextPage, result.data.length, result.totalRecords),
        );
        setPage(nextPage);
      })
      .catch(() => {
        if (sourceTokenRef.current !== token) return;
        // Keep the entries already loaded; only the first page's failure is a
        // full-screen error.
        message.error("Failed to load more activity logs.");
      })
      .finally(() => {
        if (sourceTokenRef.current !== token) return;
        setIsLoadingMore(false);
      });
  };

  if (error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={`Error ${error}`}
      />
    );
  }

  if (logs.length === 0 && !isLoading) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={"No log to display"}
      />
    );
  }

  return (
    <Space orientation="vertical" className="w-full" size={10}>
      <PageTitle level={5} title={title} style={{ margin: 0 }} />
      <Card {...cardProps}>
        <Timeline
          mode="start"
          items={
            isLoading
              ? skeletonTimeline
              : logs.map((log: ActivityLogModel) => ({
                  key: log.id,
                  color: getColorForEvent(log.event),
                  content: (
                    <Space orientation="vertical" className="w-full" size={20}>
                      <Space className="w-full space-between">
                        <Text strong>{formatLogMessage(log)}</Text>
                        <Tooltip
                          title={dayjs(log.createdAt).format(
                            "YYYY-MM-DD HH:mm",
                          )}
                        >
                          <Text type="secondary">
                            {dayjs(log.createdAt).fromNow()}
                          </Text>
                        </Tooltip>
                      </Space>
                      {log.event !== ActivityLogEventType.DELETED && (
                        <DetailsCard log={log} />
                      )}
                    </Space>
                  ),
                }))
          }
        />
        {hasMore && (
          <Space
            className="w-full"
            style={{ justifyContent: "center", marginTop: 8 }}
          >
            <Button loading={isLoadingMore} onClick={handleLoadMore}>
              Load more
            </Button>
          </Space>
        )}
      </Card>
    </Space>
  );
};

const skeletonTimeline: TimelineProps["items"] = Array.from({ length: 3 }).map(
  (_, index) => ({
    color: "gray",
    content: (
      <Space
        key={index}
        orientation="vertical"
        size={10}
        style={{ width: "100%" }}
      >
        <Skeleton.Input active style={{ height: 20 }} />
        <Skeleton.Input active block={true} style={{ height: 60 }} />
      </Space>
    ),
  }),
);

type ActivityLogTimelineType = {
  Timeline: typeof ActivityLogTimeline;
};

export { ActivityLogTimeline, type ActivityLogTimelineType };
